import Select from '@components/v2/form/Select/Select';
import Alert from '@components/v2/ui/Alert/Alert';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import LangEnum from '../../lang/LangEnum';
import SettingContainer from '../general-settings/SettingContainer';
import { OrderType } from '../orders/interfaces/orderType.enum';
import { useTranslation } from '../translation/TranslationContext';
import SatisfactionSurveyAudienceSelect from './components/SatisfactionSurveyAudienceSelect';
import SatisfactionSurveyContentFields from './components/SatisfactionSurveyContentFields';
import SatisfactionSurveyEnabledToggle from './components/SatisfactionSurveyEnabledToggle';
import SatisfactionSurveyFrequencyFields from './components/SatisfactionSurveyFrequencyFields';
import useSatisfactionSurveySettings from './useSatisfactionSurveySettings';

const orderTypeLabels: Record<OrderType, LangEnum> = {
  [OrderType.PRE_ORDER]: LangEnum.GLOBAL_PRE_ORDER,
  [OrderType.READY_FOR_DELIVERY]: LangEnum.GLOBAL_READY_FOR_DELIVERY,
  [OrderType.BOTH]: LangEnum.GLOBAL_BOTH,
};

const SatisfactionSurveySection = () => {
  const { translate } = useTranslation();
  const { settings, isDisabled, isError, isSaving, rejectedSaves, onChange } =
    useSatisfactionSurveySettings();

  const sectionProps = { settings, isDisabled, isSaving, rejectedSaves, onChange };

  const orderTypeOptions = Object.values(OrderType).map((orderType) => ({
    value: orderType,
    label: translate(orderTypeLabels[orderType]),
  }));

  return (
    <SettingContainer
      title={translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_TITLE)}
      renderInfo={() => (
        <Typography color="textSecondary">
          {translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_DESCRIPTION)}
        </Typography>
      )}
    >
      <Stack sx={{ gap: 2 }}>
        {isError && (
          <Alert severity="error" message={LangEnum.SATISFACTION_SURVEY_SETTINGS_LOAD_ERROR} />
        )}

        <SatisfactionSurveyContentFields {...sectionProps} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Select
            sx={{ minWidth: 280 }}
            disabled={isDisabled}
            label={translate(LangEnum.SATISFACTION_SURVEY_SETTINGS_ORDER_TYPE_LABEL)}
            value={settings.orderType}
            options={orderTypeOptions}
            onChange={(event) => onChange({ orderType: event.target.value as OrderType })}
          />

          <SatisfactionSurveyAudienceSelect {...sectionProps} />
        </Box>

        <SatisfactionSurveyFrequencyFields {...sectionProps} />

        <SatisfactionSurveyEnabledToggle {...sectionProps} />
      </Stack>
    </SettingContainer>
  );
};

export default SatisfactionSurveySection;
